import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Check, Loader2, Rocket, FolderKanban, Smartphone, ArrowRight, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/PhoneInput';
import { Logo } from '@/components/Logo';
import { apiFetch, getProject, makeClient, streamUrl } from '@/lib/api';
import { cn } from '@/lib/utils';
import { isOfficial, type Project } from '@/lib/projects';
import { useT } from '@/lib/i18n';

const DONE_KEY = 'bz_onboarded';

interface Instance { id: string; phone: string; status: string }

/**
 * Wizard de primeiro acesso: guia a conta a nomear o projeto e conectar o
 * primeiro número. Aparece só para admin enquanto não houver número e o usuário
 * não tiver concluído/pulado. A conclusão fica em localStorage (`bz_onboarded`).
 */
/** O trilho de QR não recebe número novo desde 2026-11 (a API recusa). */
const UNOFFICIAL_CLOSED = true;

export function Onboarding({ isAdmin }: { isAdmin: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DONE_KEY) === '1');

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async (): Promise<Project[]> => ((await apiFetch('GET', '/projects')).data as { data?: Project[] })?.data ?? [],
    enabled: isAdmin && !dismissed,
  });
  const { data: instances = [], isLoading: loadingInst } = useQuery({
    queryKey: ['instances'],
    queryFn: async (): Promise<Instance[]> => ((await makeClient().GET('/instances')).data as { data?: Instance[] })?.data ?? [],
    enabled: isAdmin && !dismissed,
  });

  const hasNumber = instances.length > 0;
  const activeId = getProject() || projects[0]?.id || '';
  const project = projects.find((p) => p.id === activeId) ?? projects[0];

  const [step, setStep] = useState<'welcome' | 'project' | 'number' | 'done'>('welcome');
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [projectNamed, setProjectNamed] = useState(false);

  useEffect(() => { if (project && !name) setName(project.name); }, [project, name]);

  // Já tem número e nunca concluiu: nada a guiar — marca como concluído.
  useEffect(() => {
    if (isAdmin && !dismissed && !loadingInst && hasNumber && step === 'welcome') {
      localStorage.setItem(DONE_KEY, '1');
      setDismissed(true);
    }
  }, [isAdmin, dismissed, loadingInst, hasNumber, step]);

  if (!isAdmin || dismissed || loadingInst || !project) return null;
  // Projeto oficial não tem QR: o número vem da Meta, e quem guia é a tela de
  // Números (OfficialSetup). Este passo a passo levaria a criar um número por QR
  // num projeto que a API recusa.
  // Projeto de QR (só os que já existiam): o trilho não recebe número novo, então
  // não há passo a passo de criação a guiar.
  if (isOfficial(project) || UNOFFICIAL_CLOSED) return null;

  const finish = () => { localStorage.setItem(DONE_KEY, '1'); setDismissed(true); };

  const saveName = async () => {
    if (!name.trim()) { toast.error(t('project.nameRequired')); return; }
    setSavingName(true);
    try {
      const r = await apiFetch('PATCH', `/projects/${project.id}`, { name: name.trim(), logo_url: '', color: '' });
      if (!r.ok) { toast.error(t('project.saveFailed')); return; }
      setProjectNamed(true);
      qc.invalidateQueries({ queryKey: ['projects'] });
      setStep('number');
    } finally { setSavingName(false); }
  };

  const tasks = [
    { key: 'project', icon: FolderKanban, label: t('onb.taskProject'), done: projectNamed },
    { key: 'number', icon: Smartphone, label: t('onb.taskNumber'), done: hasNumber },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="grid w-full max-w-3xl overflow-hidden rounded-2xl border bg-card shadow-xl md:grid-cols-[14rem_1fr]">
        {/* Trilha lateral com o checklist */}
        <aside className="hidden flex-col gap-4 border-r bg-muted/30 p-6 md:flex">
          <Logo height={24} />
          <p className="text-sm font-semibold">{t('onb.checklist')}</p>
          <ul className="flex flex-col gap-3">
            {tasks.map((task) => (
              <li key={task.key} className="flex items-center gap-2.5 text-sm">
                <span className={cn('flex size-6 items-center justify-center rounded-full border', task.done ? 'border-emerald-500 bg-emerald-500 text-white' : 'text-muted-foreground')}>
                  {task.done ? <Check className="size-3.5" /> : <task.icon className="size-3.5" />}
                </span>
                <span className={cn(task.done && 'text-muted-foreground line-through')}>{task.label}</span>
              </li>
            ))}
          </ul>
          <button onClick={finish} className="mt-auto text-left text-xs text-muted-foreground underline-offset-2 hover:underline">
            {t('onb.skip')}
          </button>
        </aside>

        {/* Conteúdo do passo */}
        <div className="flex min-h-[22rem] flex-col p-6">
          {step === 'welcome' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
              <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary"><Rocket className="size-7" /></div>
              <div>
                <h2 className="text-xl font-semibold">{t('onb.welcomeTitle')}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t('onb.welcomeSubtitle')}</p>
              </div>
              <Button onClick={() => setStep('project')} className="mt-2">{t('onb.start')} <ArrowRight className="size-4" /></Button>
            </div>
          )}

          {step === 'project' && (
            <div className="flex flex-1 flex-col gap-4">
              <div>
                <h2 className="text-lg font-semibold">{t('onb.projectTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('onb.projectSubtitle')}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('project.name')}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('onb.projectPlaceholder')} autoFocus />
              </div>
              <div className="mt-auto flex justify-end">
                <Button onClick={saveName} disabled={savingName || !name.trim()}>
                  {savingName ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />} {t('common.continue')}
                </Button>
              </div>
            </div>
          )}

          {step === 'number' && (
            <NumberStep
              projectName={name}
              hasNumber={hasNumber}
              onConnected={() => { qc.invalidateQueries({ queryKey: ['instances'] }); setStep('done'); }}
              onSkip={() => setStep('done')}
            />
          )}

          {step === 'done' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
              <div className="flex size-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500"><Check className="size-7" /></div>
              <div>
                <h2 className="text-xl font-semibold">{t('onb.doneTitle')}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{hasNumber ? t('onb.doneSubtitle') : t('onb.doneSubtitleNoNumber')}</p>
              </div>
              <Button onClick={finish} className="mt-2">{t('onb.goDashboard')} <ArrowRight className="size-4" /></Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Passo de número: cria a instância e mostra o QR ao vivo (SSE) até conectar. */
function NumberStep({
  projectName, hasNumber, onConnected, onSkip,
}: {
  projectName: string;
  hasNumber: boolean;
  onConnected: () => void;
  onSkip: () => void;
}) {
  const t = useT();
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const create = async () => {
    setCreating(true);
    try {
      const { data, error } = await makeClient().POST('/instances', { body: { phone } });
      if (error) { toast.error(t('numbers.createFailed')); return; }
      const id = (data as { id?: string })?.id ?? null;
      setInstanceId(id);
    } finally { setCreating(false); }
  };

  // Conecta via QR + SSE assim que a instância existe.
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    const render = async (code: string) => {
      const url = await QRCode.toDataURL(code, { width: 220, margin: 1 });
      if (!cancelled) setQrDataUrl(url);
    };
    makeClient().POST('/instances/{id}/connect', { params: { path: { id: instanceId }, query: { method: 'qr' } } })
      .then(({ data }) => { const code = (data as { qr_code?: string })?.qr_code; if (code) render(code); });
    const es = new EventSource(streamUrl(instanceId));
    esRef.current = es;
    es.addEventListener('qr_code', (e) => {
      const code = (JSON.parse((e as MessageEvent).data) as { code?: string }).code;
      if (code) render(code);
    });
    es.addEventListener('instance.status', (e) => {
      const s = (JSON.parse((e as MessageEvent).data) as { status?: string }).status ?? '';
      // Número novo pareia direto em "warming" (limites de envio ainda subindo), nunca "connected":
      // ambos significam pareado com sucesso, então avançamos nos dois.
      if (s === 'connected' || s === 'warming') { toast.success(t('numbers.connectedToast')); es.close(); onConnected(); }
    });
    return () => { cancelled = true; es.close(); esRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t('onb.numberTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('onb.numberSubtitle', { project: projectName })}</p>
      </div>

      {hasNumber ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
          <Check className="size-8 text-emerald-500" />
          <p className="text-sm text-muted-foreground">{t('onb.numberAlready')}</p>
        </div>
      ) : !instanceId ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t('numbers.number')}</Label>
            <PhoneInput onChange={setPhone} />
          </div>
          <Button onClick={create} disabled={creating || !phone} className="self-start">
            {creating ? <Loader2 className="size-4 animate-spin" /> : <QrCode className="size-4" />} {t('onb.addConnect')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          {qrDataUrl
            ? <img src={qrDataUrl} alt="QR Code" className="rounded-lg border bg-white p-2" />
            : <div className="flex size-[220px] items-center justify-center rounded-lg border"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>}
          <p className="text-sm text-muted-foreground">{t('numbers.connectDesc')}</p>
        </div>
      )}

      <div className="mt-auto flex justify-between">
        <Button variant="ghost" onClick={onSkip}>{t('onb.skipStep')}</Button>
        {hasNumber && <Button onClick={onConnected}>{t('common.continue')} <ArrowRight className="size-4" /></Button>}
      </div>
    </div>
  );
}
